import React, { useEffect } from 'react';
import moment from 'moment';
import {
    DiagnosisMatchStatus,
    ErrorText,
    Images,
    PermissionList,
    RolePermission,
    apptIssueType,
    useDiagnosisMatchEnabled,
} from 'utils';
import {
    apptOverlap,
    checkServiceDateAvailable,
    isStaffCredentialDateGreater,
    noActivePayCodeMessage,
    renderUnitSize,
    staffOverlap,
    timeToMinutes,
} from '../constants';
import { TypeRow } from 'components';
import { scheduleStatuses } from '../../../constants';
import {useSelector} from "react-redux";
import { diagnosisMismatchText } from './apptDiagnosis';

export const ApptErrors = ({
    inputs,
    staff,
    currentAuthorization,
    frequencyLimit,
    backError,
    createMultiple,
    setErrorList,
    errorList,
    modalDate,
    occurrence,
    recur,
    withDiagnosis,
}) => {
    const company = useSelector((state) => state.system.company);
    // Service appointment modal only (it fetches the match via ApptDiagnosis); hidden while the org switch is off
    const diagnosisEnabled = useDiagnosisMatchEnabled();
    const fetchedMatch = useSelector((state) => state.clientDiagnosis.diagnosisMatch);
    const diagnosisMatch = withDiagnosis && diagnosisEnabled && currentAuthorization ? fetchedMatch : null;
    const diagnosisIssue =
        diagnosisMatch?.status === DiagnosisMatchStatus.MISMATCH
            ? { text: <p>{`Diagnosis mismatch: ${diagnosisMismatchText(diagnosisMatch)}`}</p>, type: apptIssueType.DIAGNOSIS_MISMATCH }
            : diagnosisMatch?.status === DiagnosisMatchStatus.NO_CODES
              ? { text: <p>Missing diagnostic code: neither the client nor this service has one.</p>, type: apptIssueType.DIAGNOSTIC_CODE_MISSING }
              : null;
    const unitSize = currentAuthorization?.service?.unitSize;
    const startMinutes = inputs?.startTime && timeToMinutes(inputs?.startTime);
    const endMinutes = inputs?.endTime && timeToMinutes(inputs?.endTime);
    const maxUnit = currentAuthorization?.service?.max;
    const minUnit = currentAuthorization?.service?.min;
    const currentApptUnites = renderUnitSize(inputs, currentAuthorization);
    const checkAvailableUnits = createMultiple
        ? currentAuthorization?.availableUnits < 0
            ? -1
            : currentAuthorization?.availableUnits - currentApptUnites * occurrence
        : currentAuthorization?.availableUnits < 0
          ? -1
          : currentAuthorization?.availableUnits - currentApptUnites;
    const uniteSizeCorrect =
        inputs?.startTime && inputs?.endTime && currentAuthorization ? (endMinutes - startMinutes) % unitSize === 0 : true;
    const unitSizeCountIsCorrect =
        inputs?.startTime && inputs?.endTime && currentAuthorization ? currentApptUnites <= maxUnit && currentApptUnites >= minUnit : true;
    const tb = recur
        ? staff?.credentials?.find((i) => i?.type === 'TB')
        : staff?.verifiableCredentials && staff?.verifiableCredentials?.find((i) => i?.type === 'TB');
    const fbi = recur
        ? staff?.credentials?.find((i) => i?.type === 'DOJ')
        : staff?.verifiableCredentials && staff?.verifiableCredentials?.find((i) => i?.type === 'DOJ');
    const tbExpired = isStaffCredentialDateGreater(tb, inputs, createMultiple);
    const checkIsOut = checkServiceDateAvailable(currentAuthorization, inputs, createMultiple);
    const checkErrorProject = RolePermission([PermissionList.APPT_CREATE_WARNING?.code]);

    const warnings = [
        checkErrorProject &&
            !company?.skipTbDojCheck &&
            staff &&
            (!tb?.checked || !fbi?.checked || tbExpired) && {
                text: <p>TB/DOJ credential has issues (unchecked or expired)</p>,
                type: apptIssueType.TB_DOJ_ERROR,
            },

        checkErrorProject &&
            !uniteSizeCorrect &&
            currentAuthorization && {
                text: (
                    <p>
                        Incomplete units: Duration results in partial unit since 1 unit =<span className="bold-text">{unitSize}</span>{' '}
                        minutes.
                    </p>
                ),
            },
        checkErrorProject &&
            frequencyLimit?.frequencyOverlimit &&
            frequencyLimit?.frequencyOverlimit !== 0 &&
            currentAuthorization && {
                text: (
                    <p>
                        Frequency limit exceeded:
                        <span className="bold-text">{currentAuthorization?.frequencyLimit?.limit} units</span>
                        in
                        <span className="bold-text" style={{ textTransform: 'capitalize' }}>
                            <TypeRow text={currentAuthorization?.frequencyLimit?.mode} />
                        </span>
                        mode
                    </p>
                ),
            },
        checkErrorProject &&
            currentAuthorization &&
            frequencyLimit?.availableUnitsExceed && {
                text: (
                    <p>
                        Authorized units exceeded, available units are
                        <span className="bold-text">{currentAuthorization?.availableUnits?.toFixed(2)}</span>
                    </p>
                ),
            },
        checkErrorProject &&
            checkIsOut &&
            currentAuthorization && {
                text: (
                    <p>
                        Authorization dates out of range:
                        <span className="bold-text">{moment.utc(currentAuthorization?.startDate).format('MM/DD/YYYY')}</span> to
                        <span className="bold-text">{moment.utc(currentAuthorization?.endDate).format('MM/DD/YYYY')}</span>
                    </p>
                ),
            },
        checkErrorProject &&
            !unitSizeCountIsCorrect &&
            currentAuthorization && {
                text: (
                    <p>
                        Appointment unit count
                        <span className="bold-text">{currentApptUnites}</span>
                        is out of the allowed range
                        <span className="bold-text">{`${minUnit} - ${maxUnit}`}</span>
                    </p>
                ),
            },

        checkErrorProject && diagnosisIssue,

        modalDate?.status === scheduleStatuses?.COMPLETED && {
            text: <p>Warning! Since this appointment is already rendered, claims and processed timesheets will not be updated</p>,
        },
    ]?.filter(Boolean);

    const errors = [
        !checkErrorProject &&
            !company?.skipTbDojCheck &&
            staff &&
            (!tb?.checked || !fbi?.checked || tbExpired) && {
                text: <p>TB/DOJ credential has issues (unchecked or expired)</p>,
                type: apptIssueType.TB_DOJ_ERROR,
            },
        !checkErrorProject &&
            !uniteSizeCorrect &&
            currentAuthorization && {
                text: (
                    <p>
                        Incomplete units: Duration results in partial unit since 1 unit =<span className="bold-text">{unitSize}</span>{' '}
                        minutes.
                    </p>
                ),
            },
        !checkErrorProject &&
            frequencyLimit?.frequencyOverlimit &&
            frequencyLimit?.frequencyOverlimit !== 0 &&
            currentAuthorization && {
                text: (
                    <p>
                        Frequency limit exceeded:
                        <span className="bold-text">{currentAuthorization?.frequencyLimit?.limit} units</span>
                        in
                        <span className="bold-text" style={{ textTransform: 'capitalize' }}>
                            <TypeRow text={currentAuthorization?.frequencyLimit?.mode} />
                        </span>
                        mode
                    </p>
                ),
            },
        !checkErrorProject &&
            currentAuthorization &&
            frequencyLimit?.availableUnitsExceed && {
                text: (
                    <p>
                        Authorized units exceeded, available units are
                        <span className="bold-text">{currentAuthorization?.availableUnits?.toFixed(2)}</span>
                    </p>
                ),
            },
        !checkErrorProject &&
            checkIsOut &&
            currentAuthorization && {
                text: (
                    <p>
                        Authorization dates out of range:
                        <span className="bold-text">{moment.utc(currentAuthorization?.startDate).format('MM/DD/YYYY')}</span> to
                        <span className="bold-text">{moment.utc(currentAuthorization?.endDate).format('MM/DD/YYYY')}</span>
                    </p>
                ),
            },
        !checkErrorProject &&
            !unitSizeCountIsCorrect &&
            currentAuthorization && {
                text: (
                    <p>
                        Appointment unit count
                        <span className="bold-text">{currentApptUnites}</span>
                        is out of the allowed range
                        <span className="bold-text">{`${minUnit} - ${maxUnit}`}</span>
                    </p>
                ),
            },
        !checkErrorProject && diagnosisIssue,
        backError?.error &&
            (backError?.error === apptOverlap || backError?.error === staffOverlap) && {
                text: <p>{ErrorText.overlappingError('Appointments')}</p>,
            },
        backError?.error &&
            backError?.error === noActivePayCodeMessage && {
                text: <p>{noActivePayCodeMessage}</p>,
            },
        backError?.error &&
            backError?.error !== apptOverlap &&
            backError?.error !== staffOverlap &&
            backError?.error !== noActivePayCodeMessage && {
                text: <p>{backError?.error}</p>,
            },
    ]?.filter(Boolean);

    useEffect(() => {
        if (errors?.length !== errorList?.length && !checkErrorProject && setErrorList) {
            setErrorList(errors);
        }
    }, [errors]);

    return (
        <div>
            {!!warnings?.length && (
                <div className="appt-warning-wrapper">
                    <div className="appt-warning-count-box">
                        <img src={Images.alertTriangle} alt="warning" />
                        <p>{`Appointment has ${warnings?.length} ${errors?.length === 1 ? 'warning' : 'warnings'}`}</p>
                    </div>
                    <div className="appt-warning-box">
                        {warnings?.map(
                            (i, k) =>
                                i && (
                                    <div className="appt-warning" key={k}>
                                        <div />
                                        {i?.text}
                                        {i?.detail && (
                                            <a>
                                                View Details
                                                <img src={Images.arrowOrangeRight} alt="" />
                                            </a>
                                        )}
                                    </div>
                                )
                        )}
                    </div>
                </div>
            )}

            {!!errors?.length && (
                <div className="appt-errors-wrapper">
                    <div className="appt-error-count-box">
                        <img src={Images.error} alt="warning" />
                        <p>{`Appointment has ${errors?.length} ${errors?.length === 1 ? 'error' : 'errors'}`}</p>
                    </div>
                    <div className="appt-errors-box">
                        {errors?.map(
                            (i, k) =>
                                i && (
                                    <div className="appt-error" key={k}>
                                        <div />
                                        {i?.text}
                                        {i?.detail && (
                                            <a>
                                                View Details
                                                <img src={Images.arrowOrangeRight} alt="" />
                                            </a>
                                        )}
                                    </div>
                                )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
